import React, { useEffect, useMemo } from 'react';
import { formatGroupLabel, formatOptionLabel, MenuOptionType, TeamMultiSelectProps } from "./TeamMultiSelect";
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { useDispatch, useSelector } from "react-redux";
import {
  loadFollowingGroups,
  selectFollowingWorkGroups,
  selectIsLoading,
  updateFollowingTeamsAndWorkgroups
} from '@features/whereabouts/store/followingTeamsAndTags.duck';
import styled from "styled-components";
import Select from "react-select";
import { DROP_DOWN_STYLES } from "../drop-down-list/DropDownStyles";
import makeAnimated from "react-select/animated";
import { useTranslation } from "react-i18next";
import {
  FAVOURITES_OPTION,
  generateLineReportOptions,
  groupTeams,
  groupWorkgroups,
  LINE_REPORT_OPTION
} from "./GroupTeamsAndWorkgroupsByOffices";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { WithFollowedTeams } from "./WithFollowedTeams";
import { dedup } from '@shared/utils/ArrayUtils';

enum Side { Left, Right }

const animatedComponents: any = makeAnimated();

interface Props extends TeamMultiSelectProps {
  style?: any;
  outerClassName?: string;
  reloadTeamWhereaboutsOnSelect: boolean;
  includeLineReportOption?: boolean
  isLineReportOptionSelected?: boolean;
  isFavouritedUsersOptionSelected?: boolean;
}

export function TeamMultiSelectWithFavourites(props: Props) {
  const {teams, workGroups, reloadTeamWhereaboutsOnSelect, includeLineReportOption, isLineReportOptionSelected, isFavouritedUsersOptionSelected} = props;
  const dispatch = useDispatch();
  const selectedWorkGroups = useSelector(selectFollowingWorkGroups);
  const selectedTeams = WithFollowedTeams();
  const isLoading = useSelector(selectIsLoading);
  const allOffices = useSelector(selectAllOfficeEntities);
  const {t} = useTranslation();

  useEffect(() => {
    dispatch(loadFollowingGroups(false));
  }, [dispatch]);

  const groupedOptions = useMemo(() => {
    let groupedTeams = !!teams ? groupTeams(teams, allOffices) : [];
    let groupedWorkgroups = !!workGroups ? groupWorkgroups(workGroups, allOffices) : [];
    let lineReportOptions = generateLineReportOptions(!!includeLineReportOption);

    return [
      ...lineReportOptions,
      ...groupedTeams,
      ...groupedWorkgroups,
    ]
  }, [allOffices, includeLineReportOption, teams, workGroups]);

  const onGroupSelected = (selectedTeams: TeamEntity[], selectedWorkGroups: Tag[], showLineReports: boolean, showFavourites: boolean) => {
    dispatch(updateFollowingTeamsAndWorkgroups({teams: selectedTeams, workGroups: selectedWorkGroups, reloadTeamWhereabouts: reloadTeamWhereaboutsOnSelect}))
    props.onSelect?.(selectedTeams, selectedWorkGroups, [], [], showLineReports, showFavourites);
  }

  const selectedItems = useMemo(() => {
    const selectedTeamIds = selectedTeams.filter(Boolean).map(st => st.id);
    const selectedWorkGroupIds = selectedWorkGroups.filter(Boolean).map(st => st.id);
    let lineReportOptions = [];
    if (isLineReportOptionSelected) lineReportOptions.push(LINE_REPORT_OPTION);
    if (isFavouritedUsersOptionSelected) lineReportOptions.push(FAVOURITES_OPTION);

    const flatGroupOptions = groupedOptions.filter(go => go.options)
      .map(go => go.options)
      .flat()
      .filter(go => go.value)

    const selectedTeamOptions = flatGroupOptions.filter((ddt: any) => selectedTeamIds.includes(ddt?.value?.id ?? ''))
    const selectedGroupOptions = flatGroupOptions.filter((ddt: any) => selectedWorkGroupIds.includes(ddt?.value?.id ?? ''))

    return [
      ...lineReportOptions,
      ...selectedTeamOptions,
      ...selectedGroupOptions,
    ]
  }, [groupedOptions, isFavouritedUsersOptionSelected, isLineReportOptionSelected, selectedTeams, selectedWorkGroups]);

  const onItemSelected = (selection: any) => {
    const filteredSelection = dedup(selection, (p1: any, p2: any) => p1?.value?.id === p2?.value?.id)
      .filter((item: any) => item.type === MenuOptionType.Team || item.type === MenuOptionType.WorkGroup)
    const showLineReports = selection.filter((s: any) => s.type === MenuOptionType.LineReports).length > 0;
    const showFavourites = selection.filter((s: any) => s.type === MenuOptionType.Favourites).length > 0;
    if (Array.isArray(filteredSelection)) {
      const teams = filteredSelection.filter(s => s.type === MenuOptionType.Team).map(i => i.value);
      const workGroups = filteredSelection.filter(s => s.type === MenuOptionType.WorkGroup).map(i => i.value);
      onGroupSelected(teams, workGroups, showLineReports, showFavourites);
    }
  }

  return (
    <Row style={props?.style ?? {}} className={props?.outerClassName ?? ''}>
      <Select
        className={props.multiSelectClassName ?? ' teamMultiSelect__Dropdown'}
        value={selectedItems}
        options={groupedOptions as any}
        isDisabled={isLoading}
        isLoading={isLoading}
        formatGroupLabel={formatGroupLabel as any}
        formatOptionLabel={formatOptionLabel}
        onChange={onItemSelected}
        styles={{
          ...DROP_DOWN_STYLES,
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        placeholder={t('Select...')}
        closeMenuOnSelect={!!props.singleSelect}
        components={{ ...animatedComponents, ClearIndicator:() => null, IndicatorSeparator:() => null }}
        isMulti={!props.singleSelect}
      />
    </Row>
  )
}

const Row = styled.div`
  display: flex;
  .teamMultiSelect__Dropdown {
    min-width: 325px !important;
    & > div {
      min-width: 325px !important;
    }
  }
`
